import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { createSparePartType, updateSparePartType } from "../../../fetchers/cmmsSparePartType";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";
import cmmsMessages from "../../cmms/cmmsMessages";
import FieldError from "../../cmmsSpareParts/components/FieldError";
import messages from "../messages";

/*
 * Add or rename a spare part type.
 *
 * One field, because the table is one column plus its state. IsActive is not here for the same
 * reason it is absent from SparePartTypeUpdateRequestViewModel: retiring a type has consequences
 * for every part carrying it, and it has an endpoint rather than a checkbox an edit could tick in
 * passing.
 *
 * Renaming is a rename, not a retire-and-recreate. The parts pointing at the row follow it, which
 * is the whole reason types are a table rather than free text on the part.
 */
const emptyForm = { name: "" };

export default function SparePartTypeFormDialog({ open, onClose, sparePartType, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const isEdit = Boolean(sparePartType?.id);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  /*
   * Required, not optional. AxionControls.AxionTextBox is uncontrolled — it passes `value` to
   * TextField as defaultValue — so the field is seeded when the dialog mounts and there is no
   * second chance to fill it from a later render.
   */
  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? { name: sparePartType.name ?? "" } : emptyForm);
    setErrors({});
  }, [open, isEdit, sparePartType]);

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    setForm((current) => ({ ...current, [name || id]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => (isEdit
      ? updateSparePartType(sparePartType.id, payload)
      : createSparePartType(payload)),
  });

  /*
   * Name required, and nothing else.
   *
   * Uniqueness is deliberately not checked here. The server holds a unique index on
   * (CompanyKey, Name) and answers a clash with CMMS_SPARE_PART_TYPE_NAME_TAKEN and the name
   * already taking it — a check in the browser would have to read the whole list, would miss the
   * inactive rows that also hold their names, and would still have to lose a race to the index.
   */
  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = intl.formatMessage(messages.validationNameRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      companyKey,
      name: form.name.trim(),
      // No created/updatedByUserId — the server takes it from the token.
    };

    try {
      await saveMutation.mutateAsync(payload);
      // Every filter variant, because a new type belongs in whichever list is on screen and a
      // rename changes the sort. The spare part form's type dropdown reads the same key.
      await queryClient.invalidateQueries({ queryKey: ["cmmsSparePartTypes"] });
      pushNotification(intl.formatMessage(
        isEdit ? messages.sparePartTypeToastUpdated : messages.sparePartTypeToastCreated,
      ));
      onClose?.();
    } catch (error) {
      // The server's own words: a duplicate says which name, which no message written in advance
      // could.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.sparePartTypeSaveFailed)));
    }
  };

  const submitting = saveMutation.isPending || saveMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(
        isEdit ? messages.sparePartTypeFormEditTitle : messages.sparePartTypeFormAddTitle,
      )}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <AxionControls.AxionTextBox
            name="name"
            label={intl.formatMessage(messages.fieldName)}
            value={form.name}
            onChange={handleTextChange}
          />
          <FieldError message={errors.name} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.sparePartTypeFieldNameHint} />
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
